"use client";

import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Colors } from "@/theme";
import Image from "next/image";
import CrossSVG from "@/assets/svgs/cross.svg";

/**
 * Types
 */

export interface SimpleToastProps {
  message: string;
  duration?: number;
  onClose: () => void;
}

/**
 * Styled Components
 */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const ToastContainer = styled.div<{
  $isVisible: boolean;
}>`
  position: fixed;
  bottom: 20px;
  left: 20px;
  background-color: ${Colors.blackPurple};
  color: ${Colors.white};
  padding: 12px 16px;
  border-radius: 8px;
  min-width: 250px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)} 0.5s
    ease-in-out;
  transition: opacity 0.5s ease-in-out;

  font-family: ${({ theme }) => theme.Fonts.ttNormsProNormal};
  font-size: 14px;
  font-style: normal;
  line-height: 18px;
`;

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.graySmoke500};
  cursor: pointer;
  margin-left: 10px;
`;

const Text = styled.p`
  margin: 0;
`;

/**
 * Constants
 */

export const SIMPLE_TOAST_DURATION = 3000;

/**
 * SimpleToast Component
 */

const SimpleToast = ({
  message,
  duration = SIMPLE_TOAST_DURATION,
  onClose,
}: SimpleToastProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 500);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return isVisible ? (
    <ToastContainer $isVisible={isVisible} data-testid="simple-toast">
      <ToastContent>
        <Text dangerouslySetInnerHTML={{ __html: message }} />
      </ToastContent>
      <CloseButton
        onClick={onClose}
        data-testid="simple-toast-close-button"
      >
        <Image src={CrossSVG} alt="cross" width={12} height={12} />
      </CloseButton>
    </ToastContainer>
  ) : null;
};

export default SimpleToast;
